import React, { createContext, useContext, useState, useEffect } from 'react';
import { authService } from '../services/api';

// Création du contexte d'authentification
const AuthContext = createContext();

// Hook personnalisé pour utiliser le contexte d'authentification
export const useAuth = () => useContext(AuthContext);

// Fournisseur du contexte d'authentification
export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Charger l'utilisateur au montage du composant
  useEffect(() => {
    const loadUser = async () => {
      try {
        if (localStorage.getItem('token')) {
          const userData = await authService.getCurrentUser();
          
          // S'assurer que l'utilisateur a un ID accessible
          const userWithId = {
            ...userData,
            // Normaliser l'ID pour qu'il soit toujours accessible via la propriété 'id'
            id: userData._id || userData.id
          };
          
          setCurrentUser(userWithId);
        }
      } catch (err) {
        console.error('Erreur lors du chargement de l\'utilisateur:', err);
        // Si l'erreur est une réponse HTML (erreur 500 du serveur), ne pas afficher l'erreur complète
        if (err.response && err.response.headers && err.response.headers['content-type']?.includes('text/html')) {
          console.error('Le serveur a retourné une erreur HTML au lieu de JSON. Vérifiez les logs du backend.');
        }
        localStorage.removeItem('token');
        setCurrentUser(null);
      } finally {
        setLoading(false);
      }
    };

    loadUser();
  }, []);

  // Fonction d'inscription
  const register = async (userData) => {
    setError(null);
    try {
      const response = await authService.register(userData);
      return response;
    } catch (err) {
      setError(err.message || 'Erreur lors de l\'inscription');
      throw err;
    }
  };

  // Fonction de connexion
  const login = async (credentials) => {
    setError(null);
    try {
      // Connexion de l'utilisateur
      await authService.login(credentials);
      // Récupération des données utilisateur après connexion
      const userData = await authService.getCurrentUser();
      
      // S'assurer que l'utilisateur a un ID accessible
      const userWithId = {
        ...userData,
        // Normaliser l'ID pour qu'il soit toujours accessible via la propriété 'id'
        id: userData._id || userData.id
      };
      
      setCurrentUser(userWithId);
      return userWithId;
    } catch (err) {
      setError(err.message || 'Identifiants invalides');
      throw err;
    }
  };

  // Fonction de déconnexion
  const logout = () => {
    authService.logout();
    setCurrentUser(null);
  };

  // Fonction de vérification d'email
  const verifyEmail = async (token) => {
    setError(null);
    try {
      const response = await authService.verifyEmail(token);
      return response;
    } catch (err) {
      setError(err.message || 'Erreur lors de la vérification de l\'email');
      throw err;
    }
  };

  // Fonction de renvoi d'email de vérification
  const resendVerificationEmail = async (email) => {
    setError(null);
    try {
      const response = await authService.resendVerificationEmail(email);
      return response;
    } catch (err) {
      setError(err.message || 'Erreur lors de l\'envoi de l\'email de vérification');
      throw err;
    }
  };

  // Fonction de demande de réinitialisation de mot de passe
  const forgotPassword = async (email) => {
    setError(null);
    try {
      const response = await authService.forgotPassword(email);
      return response;
    } catch (err) {
      setError(err.message || 'Erreur lors de la demande de réinitialisation de mot de passe');
      throw err;
    }
  };

  // Fonction de réinitialisation de mot de passe
  const resetPassword = async (token, password) => {
    setError(null);
    try {
      const response = await authService.resetPassword(token, password);
      return response;
    } catch (err) {
      setError(err.message || 'Erreur lors de la réinitialisation du mot de passe');
      throw err;
    }
  };

  // Valeur du contexte
  const value = {
    currentUser,
    loading,
    error,
    register,
    login,
    logout,
    verifyEmail,
    resendVerificationEmail,
    forgotPassword,
    resetPassword
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
